import React from "react";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000ActionDropdownBtn from "../../../../u2000Core/components/buttons/U2000ActionDropdownBtn";
import U2000ResponsiveToolbar from "../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import { FaExchangeAlt, FaFilePdf, FaLock, FaLockOpen, FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";
import { useU2000AuthContext } from "../../../../u2000Core/context/auth/U2000UseAuthContext";
import type { U2000FormStateHandlers } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumState } from "../../../../u2000Core/reducer/formReducer/U2000FormState";
import { NS, U3121ROLES } from "../../../U3121";
import type { U3121Project } from "../../../U3121Service";
import { useU3121Context } from "../../../U3121Context";

/** Normalise la langue i18n en "fr" | "en" pour l'API. */
function reportLangFromI18n(language: string): "fr" | "en" {
  const base = language.substring(0, 2).toLowerCase();
  return base === "en" ? "en" : "fr";
}

interface U3121ProjectFormToolBarProps {
  formStateHandlers: U2000FormStateHandlers<U3121Project> & {
    addNewProject: () => void;
    modifyProject: () => void;
    deleteProject: () => void;
    changeStatusProject: () => void | Promise<void>;
    convertToProject: () => void | Promise<void>;
    exportProjectOpeningFormPdf: (projectId: string, lang?: "fr" | "en") => void;
  };
}

export const U3121ProjectFormToolBar: React.FC<U3121ProjectFormToolBarProps> = ({ formStateHandlers }) => {
  const formState = formStateHandlers.getFormState();
  const { hasRole } = useU2000AuthContext();
  const { initLU } = useU3121Context();
  const { t, i18n } = useTranslation();
  const project = formStateHandlers.getData();
  const isActive = project?.projectStatus === true;
  const changeStatusLabel = isActive ? t("btnClose", { ns: NS }) : t("btnReopen", { ns: NS });
  const includeSubProjet = project?.includeSubProjet === true;
  const convertToLabel = includeSubProjet ? t("btnConvertToProject", { ns: NS }) : t("btnConvertToSubProject", { ns: NS });
  const reportLang = reportLangFromI18n(i18n.language);
  const showIncludeSubProject = initLU.personalization?.admIncludeSubProject === "true";

  const actions = [
    {
      key: "add",
      label: t("btnAdd", { ns: NS }),
      hasRole: hasRole(U3121ROLES.ADD),
      handleClick: () => formStateHandlers.addNewProject(),
      disabled: formState === U2000FormStateEnumState.EDIT,
      icon: FaPlus,
      Component: U2000ActionBtn,
    },
    {
      key: "modify",
      label: t("btnModify", { ns: NS }),
      hasRole: hasRole(U3121ROLES.EDIT),
      handleClick: () => formStateHandlers.modifyProject(),
      disabled: formState === U2000FormStateEnumState.EDIT,
      icon: FaRegFile,
      Component: U2000ActionBtn,
    },
    {
      key: "delete",
      label: t("btnDelete", { ns: NS }),
      hasRole: hasRole(U3121ROLES.DELETE),
      handleClick: () => formStateHandlers.deleteProject(),
      disabled: formState === U2000FormStateEnumState.EDIT,
      icon: FaTrashAlt,
      Component: U2000ActionBtn,
    },
    {
      key: "changeStatus",
      label: changeStatusLabel,
      hasRole: hasRole(U3121ROLES.EDIT),
      handleClick: () => formStateHandlers.changeStatusProject(),
      disabled: formState === U2000FormStateEnumState.EDIT || !project?.id,
      icon: isActive ? FaLock : FaLockOpen,
      Component: U2000ActionBtn,
    },
    ...(showIncludeSubProject
      ? [
          {
            key: "convertTo",
            label: convertToLabel,
            hasRole: hasRole(U3121ROLES.EDIT),
            handleClick: () => formStateHandlers.convertToProject(),
            disabled: formState === U2000FormStateEnumState.EDIT || !project?.id || project.projectStatus === false,
            icon: FaExchangeAlt,
            Component: U2000ActionBtn,
          },
        ]
      : []),
    {
      key: "report",
      label: t("btnReport", { ns: NS }),
      hasRole: hasRole(U3121ROLES.READ),
      disabled: !project?.id || formState === U2000FormStateEnumState.EDIT,
      icon: FaFilePdf,
      Component: U2000ActionDropdownBtn,
      menuAlign: "left",
      handleClick: () => {},
      items: [
        {
          key: "opening",
          label: t("reportProjectOpening", { ns: NS }),
          icon: FaFilePdf,
          handleClick: () => project?.id && formStateHandlers.exportProjectOpeningFormPdf(project.id, reportLang),
        },
      ],
    },
  ];

  return <U2000ResponsiveToolbar actions={actions} />;
};
